import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, StateWord, WaitingChip } from "@/design/primitives";

// State word + tone by machine state (council token re-map; gold/bronze = chip grammar only).
const STATE_WORD = {
  INTAKE: { word: "New idea", tone: "muted" },
  PLAN: { word: "Taking shape", tone: "amber" },
  EXECUTE: { word: "Being built", tone: "green" },
  TEST: { word: "Being verified", tone: "amber" },
  CLOSE: { word: "Wrapping up", tone: "chip" },
  ARCHIVED: { word: "Done", tone: "dimmed" },
  UNSORTED: { word: "Needs sorting", tone: "amber" },
};
export { STATE_WORD };

const LINE_TONE = { green: "text-os-green", amber: "text-os-amber", muted: "text-os-ink-3" };

export const GoalHeading = ({ card, as: Title = "h3", className }) => (
  <div className={className}>
    <div className="flex items-baseline justify-between gap-3">
      <Title className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
        {card.title}
      </Title>
      <StateWord tone={STATE_WORD[card.rawState]?.tone || "muted"}>
        {STATE_WORD[card.rawState]?.word || card.stateWord}
      </StateWord>
    </div>
    <Label as="p" data-testid="goal-card-brand" className="mt-1">
      {card.brand}
    </Label>
  </div>
);

export const GoalStateLine = ({ card, className }) =>
  card.rawState === "CLOSE" ? (
    <div className={className}>
      <WaitingChip>{card.stateWord}</WaitingChip>
    </div>
  ) : (
    <p
      data-testid="goal-card-line"
      className={cn(
        "text-[0.875rem] leading-[1.45]",
        LINE_TONE[card.stateTone] || "text-os-ink-2",
        className,
      )}
    >
      {card.stateWord}
    </p>
  );

export const GoalNext = ({ card, className }) => (
  <div className={cn("mt-3 flex flex-col gap-2", className)}>
    <p data-testid="goal-card-next" className="text-[0.875rem] font-medium leading-[1.45] text-os-ink">
      {card.next}
    </p>
    <p
      data-testid="goal-card-blockers"
      className={cn(
        "text-[0.8125rem] leading-[1.4]",
        card.blockersLine.startsWith("Waiting on:") ? "text-os-amber" : "text-os-ink-3",
      )}
    >
      {card.blockersLine}
    </p>
    {card.needsDusting && (
      <p data-testid="goal-card-dusting" className="text-[0.8125rem] font-medium text-os-amber">
        Needs dusting — hasn't moved in a while
      </p>
    )}
  </div>
);

const CheckMeta = ({ card }) => {
  const { checksVerified: v, checksTotal: t, closeLeft } = card;
  return (
    <MonoMeta as="p" data-testid="goal-card-count">
      {t > 0 && `${v} of ${t} checks verified`}
      {t > 0 && closeLeft > 0 && " · "}
      {closeLeft > 0 ? `close-out: ${closeLeft} items left` : null}
      {t === 0 && closeLeft === 0 ? `${card.phaseCount} planned steps` : null}
    </MonoMeta>
  );
};

export const GoalCard = ({ card, onOpen }) => {
  const open = () => onOpen(card.slug);
  const archived = card.rawState === "ARCHIVED";
  return (
    <Card
      role="button"
      tabIndex={0}
      quiet={archived || card.rawState === "INTAKE"}
      data-testid={`goal-card-${card.slug}`}
      data-state={card.rawState}
      aria-label={`${card.title}, ${card.brand}. Open details`}
      onClick={open}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), open())}
      className={cn(
        "flex h-full cursor-pointer flex-col p-5 text-left hover:border-os-border-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink",
        archived && "opacity-70",
      )}
    >
      <GoalHeading card={card} />
      <GoalStateLine card={card} className="mt-3 flex-1" />
      <GoalNext card={card} />
      <div className="mt-4 border-t border-os-border pt-4">
        <CheckMeta card={card} />
      </div>
      <MonoMeta as="p" data-testid="goal-card-stamp" className="mt-3">
        checked {card.asOf}
      </MonoMeta>
    </Card>
  );
};